import React from 'react';

import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { Formik, Form as FormikForm, useFormikContext } from 'formik';

import { useAppSelector } from 'hooks';
import { Input, Select, TextAreaField } from 'components/Forms';
import { SeverityIcon } from 'components/UI';
import { buildObservationsOptions } from 'utils/MeasurementsStoreFunctions';
import { buildQuestionnaireOptions } from 'utils/Questionnaires';
import { getSeveritySvg } from 'utils/Icons';
import { SmartEventActionType } from 'constants/enums';
import {
  getQuestionnaires,
  getCurrentGroup,
  getObservationCollections
} from 'store/groups/reducer';

interface ISmartEventsBuilderProps {
  close: any;
  show: boolean;
  conditions: ISmartEventConditionForm[];
}

interface BuilderFormProps {
  valid: boolean;
}

const AddSmartEventActionModal = ({ close, show, conditions }: ISmartEventsBuilderProps) => {
  const intl = useIntl();
  const selectedGroup = useAppSelector(getCurrentGroup);
  const observationCollections = useAppSelector(getObservationCollections);
  const groupQuestionnaires = useAppSelector(getQuestionnaires);
  const observationsToSelect: ISelectOption[] = buildObservationsOptions(observationCollections);
  const complianceSMSreminders = selectedGroup?.mobileAndSmsMessaging === '1';

  const defaultValues: ISmartEventActionForm = {
    actionType: '',
    alertType: '',
    alertLevel: '',
    alertTitle: '',
    alertDescription: '',
    questionnaireAction: '',
    measurementClassAction: '',
    messageAction: ''
  };

  const actionTypeOptions: ISelectOption[] = [
    {
      label: intl.formatMessage({ id: 'alertForClinicianKey' }),
      value: SmartEventActionType.ALERT
    },
    {
      label: intl.formatMessage({ id: 'measurementTaskKey' }),
      value: SmartEventActionType.MEASUREMENT
    },
    {
      label: intl.formatMessage({ id: 'questionnaireTaskKey' }),
      value: SmartEventActionType.QUESTIONNAIRE
    },
    {
      label: intl.formatMessage({ id: 'messageToPatientKey' }),
      value: SmartEventActionType.MESSAGE
    }
  ];

  const alertTypes: ISelectOption[] = [
    {
      label: intl.formatMessage({ id: 'medicalLabelKey' }),
      value: 'medical'
    },
    {
      label: intl.formatMessage({ id: 'medicalComplianceKey' }),
      value: 'compliance'
    }
  ];

  const alertLevels: ISwitchOption[] = [
    {
      label: `${intl.formatMessage({ id: 'priorityKey' })} 1`,
      icon: <SeverityIcon name={getSeveritySvg('50', '')} />,
      value: '50'
    },
    {
      label: `${intl.formatMessage({ id: 'priorityKey' })} 2`,
      icon: <SeverityIcon name={getSeveritySvg('30', '')} />,
      value: '30'
    },
    {
      label: `${intl.formatMessage({ id: 'priorityKey' })} 3`,
      icon: <SeverityIcon name={getSeveritySvg('20', '')} />,
      value: '20'
    },
    {
      label: `${intl.formatMessage({ id: 'priorityKey' })} 4`,
      icon: <SeverityIcon name={getSeveritySvg('15', '')} />,
      value: '15'
    },
    {
      label: `${intl.formatMessage({ id: 'priorityKey' })} 5`,
      icon: <SeverityIcon name={getSeveritySvg('10', '')} />,
      value: '10'
    }
  ];

  const handleValidation = (values: ISmartEventActionForm) => {
    const errors: any = {};

    if (!values.actionType) {
      errors.actionType = intl.formatMessage({ id: 'FieldNotEmptyKey' });
    } else {
      switch (values.actionType) {
        case SmartEventActionType.ALERT:
          if (!values.alertLevel) {
            errors.alertLevel = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          if (!values.alertType) {
            errors.alertType = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          if (!values.alertTitle) {
            errors.alertTitle = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          break;
        case SmartEventActionType.MEASUREMENT:
          if (!values.measurementClassAction) {
            errors.measurementClassAction = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          break;
        case SmartEventActionType.QUESTIONNAIRE:
          if (!values.questionnaireAction) {
            errors.questionnaireAction = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          break;
        case SmartEventActionType.MESSAGE:
          if (!values.messageAction) {
            errors.messageAction = intl.formatMessage({ id: 'FieldNotEmptyKey' });
          }
          break;
      }
    }

    return errors;
  };

  const handleSubmit = (values: ISmartEventActionForm) => {
    close(values);
  };

  const BuilderForm = ({ valid }: BuilderFormProps) => {
    const { values } = useFormikContext<ISmartEventActionForm>();

    return (
      <FormikForm autoComplete="off">
        <Row>
          <Form.Group className="form-group" as={Col}>
            <Select
              dataTestId="action-type-dd"
              variant="form"
              label={intl.formatMessage({ id: 'actionTypeKey' })}
              name="actionType"
              options={actionTypeOptions}
              isSearchable={false}
              required
            />
          </Form.Group>
        </Row>
        {values.actionType === SmartEventActionType.ALERT && (
          <>
            <Row>
              <Form.Group className="form-group" as={Col} md={12} lg={6}>
                <Select
                  dataTestId="alert-type-dd"
                  name="alertType"
                  variant="form"
                  label={intl.formatMessage({ id: 'alertTypeKey' })}
                  options={alertTypes}
                  isSearchable={false}
                  required
                />
              </Form.Group>
            </Row>
            <Row>
              <Form.Group className="form-group" as={Col} md={12} lg={6}>
                <Select
                  dataTestId="alert-level-dd"
                  name="alertLevel"
                  variant="form"
                  label={intl.formatMessage({ id: 'levelKey' })}
                  options={alertLevels}
                  isSearchable={false}
                  useIcon
                  required
                />
              </Form.Group>
            </Row>
            <Row>
              <Form.Group className="form-group" as={Col} md={12}>
                <Input
                  data-test="alert-title-input"
                  type="text"
                  label={intl.formatMessage({ id: 'titleKey' })}
                  name="alertTitle"
                  required
                />
              </Form.Group>
            </Row>
            <Row>
              <Form.Group as={Col} md={12} className="form-group mb-0">
                <Input
                  data-test="alert-description-input"
                  as="textarea"
                  label={intl.formatMessage({ id: 'descriptionKey' })}
                  name="alertDescription"
                />
              </Form.Group>
            </Row>
          </>
        )}
        {values.actionType === SmartEventActionType.MEASUREMENT && (
          <Row>
            <Form.Group className="form-group" as={Col} md={12} lg={6}>
              <Select
                dataTestId="measurement-dd"
                name="measurementClassAction"
                variant="form"
                label={intl.formatMessage({ id: 'measurementKey' })}
                options={observationsToSelect}
                containerNoWrap
                required
              />
            </Form.Group>
          </Row>
        )}
        {values.actionType === SmartEventActionType.QUESTIONNAIRE && (
          <Row>
            <Form.Group className="form-group" as={Col} md={12} lg={6}>
              <Select
                dataTestId="questionnaire-dd"
                name="questionnaireAction"
                variant="form"
                label={intl.formatMessage({ id: 'surveyTitleKey' })}
                options={buildQuestionnaireOptions(groupQuestionnaires)}
                containerNoWrap
                required
              />
            </Form.Group>
          </Row>
        )}
        {values.actionType === SmartEventActionType.MESSAGE && (
          <>
            <Row>
              <Form.Group className="form-group" as={Col} md={12}>
                <TextAreaField
                  data-test="message-input"
                  label={intl.formatMessage({ id: 'message' })}
                  name="messageAction"
                  rows={4}
                  required
                />
              </Form.Group>
            </Row>
            {complianceSMSreminders && (
              <Row>
                <Col md={12}>
                  <Alert variant="warning" className="w-100 ">
                    <Alert.Heading className="text-sm">
                      {intl.formatMessage({ id: 'patientSMSEnabledKey' })}
                    </Alert.Heading>
                    {intl.formatMessage({ id: 'messagePIIWarningKey' })}
                  </Alert>
                </Col>
              </Row>
            )}
          </>
        )}
        <hr />
        <div className="text-right">
          <Button variant="link" onClick={() => close()} data-test="cancel-btn">
            {intl.formatMessage({ id: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            type="submit"
            className="ms-3"
            disabled={!valid}
            data-test="submit-btn"
          >
            {intl.formatMessage({ id: 'add' })}
          </Button>
        </div>
      </FormikForm>
    );
  };

  return (
    <Modal show={show} onHide={close} backdrop="static" data-test="smart-event-add-action-modal">
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({ id: 'addActionKey' })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {' '}
        <Formik initialValues={defaultValues} validate={handleValidation} onSubmit={handleSubmit}>
          {(formik) => {
            return <BuilderForm valid={formik.isValid} />;
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddSmartEventActionModal;
